import React, { useId, useState } from 'react';
import { Gate } from '../api';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import './GateMarker.css';
import { getCardBackPath } from '../utils/cardBackArtwork';

export const getGateBackPath = (style: string) => getCardBackPath('gates', style);
export const useGateBackPath = () => getGateBackPath(useMonsterTokenStyle());

const traits = new Set(['Blood', 'Devouring', 'Doom', 'Endless', 'Madness', 'Monstrous', 'Moving']);
const worldLabel = (name: string) => name === 'Abyss' ? 'The Abyss' : name === 'Rlyeh' ? 'R’lyeh'
  : name === 'CityOfTheGreatRace' ? 'The City of the Great Race'
  : name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\bOf\b/g, 'of').replace(/\bThe\b/g, 'the').replace(/^the /, 'The ');
const preview = (name: string) => `/OtherWorlds/rework/small/${encodeURIComponent(name)}.jpg`;

type Props = { gate: Gate; classicPath: string; alt: string; className?: string };

/** The wrapper owns size and interactions; both artwork styles fill exactly the same box. */
export function GateArtwork({ gate, classicPath, alt, className = '' }: Props) {
  const style = useMonsterTokenStyle();
  const id = `gate-${useId().replace(/:/g, '')}`;
  const sources = [preview(gate.otherWorld), ...(gate.secondaryOtherWorld ? [preview(gate.secondaryOtherWorld)] : [])];
  const sourceKey = sources.join('|');
  const [failedSource, setFailedSource] = useState('');
  if (style === 'Classic' || failedSource === sourceKey) {
    return <img loading="lazy" src={classicPath} alt={alt} className={className} />;
  }
  const split = !!gate.secondaryOtherWorld;
  const names = [gate.otherWorld, ...(gate.secondaryOtherWorld ? [gate.secondaryOtherWorld] : [])];
  const signs = [gate.dimensionSign, ...(split ? [gate.secondaryDimensionSign] : [])];
  return <svg viewBox="0 0 240 240" role="img" aria-label={alt}
    className={`gate-digital-artwork ${className}`} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id={`${id}-rim`}><stop stopColor="#fffbe0" /><stop offset=".83" stopColor="#f3e6b9" /><stop offset="1" stopColor="#c8b477" /></radialGradient>
      <clipPath id={`${id}-circle`}><circle cx="120" cy="120" r="98" /></clipPath>
      <path id={`${id}-top`} d="M 27,120 A 93,93 0 0,1 213,120" />
      <path id={`${id}-bottom`} d="M 19,120 A 101,101 0 0,0 221,120" />
    </defs>
    <circle cx="120" cy="120" r="119" fill={`url(#${id}-rim)`} stroke="#655733" strokeWidth="2" />
    <g clipPath={`url(#${id}-circle)`}>
      {sources.map((src, index) => <image key={src} href={src} x="22" y={split ? 22 + index * 98 : 22}
        width="196" height={split ? 98 : 196} preserveAspectRatio="xMidYMid slice"
        onError={() => setFailedSource(sourceKey)} />)}
      {split && <path d="M20 120H220" stroke="#f8edc9" strokeWidth="5" />}
    </g>
    <g className="gate-name-bands" fill="none" stroke="white" strokeOpacity=".82" strokeWidth="30" aria-hidden="true">
      <path d="M 18,120 A 102,102 0 0,1 222,120" />
      {split && <path d="M 26,120 A 94,94 0 0,0 214,120" />}
    </g>
    {names.map((name, index) => <text key={index} fill="#241f16" fontFamily="Georgia, serif" fontWeight="bold"
      fontSize={Math.min(17, 245 / worldLabel(name).length + 6)} textAnchor="middle">
      <textPath href={`#${id}-${index ? 'bottom' : 'top'}`} startOffset="50%">{worldLabel(name)}</textPath>
    </text>)}
    {signs.map((sign, index) => <image key={index} data-gate-sign={sign}
      href={`/signs/${encodeURIComponent(sign ?? '')}.svg`} x={split ? 32 : 101} y={split ? 73 + index * 58 : 184}
      width="38" height="38" />)}
    <text x="202" y={split ? 132 : 157} textAnchor="middle" fill="#bf302b" stroke="#fff7dd" strokeWidth="5"
      paintOrder="stroke" fontFamily="Georgia, serif" fontSize="35" fontWeight="bold">
      {gate.modifier >= 0 ? '+' : ''}{gate.modifier}
    </text>
    {gate.ability && traits.has(gate.ability) && <g>
      <circle cx="201" cy="179" r="16" fill="#fff4d4" />
      <image data-gate-trait={gate.ability} href={`/Gates/traits/${gate.ability}.svg`} x="188" y="166" width="26" height="26" />
    </g>}
  </svg>;
}
